import React, { useState, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Modal, Form, Button, Alert, Accordion } from "react-bootstrap";
import Select from "react-select";
import NumUsersDisplay from "../../../../components/wifi/NumUsersDisplay";
import useAuth from "../../../../hooks/useAuth";
import { apiConfig } from "../../../../config";
import NotyfContext from "../../../../contexts/NotyfContext";
import MultipleUserCsvInput from "../MultipleUserCsvInput";
import { createRateLimitedAxios } from "utils/ratelimit";

const AddMultipleNew8021XCredentialModal = ({
  openModals,
  toggle,
  customerOptions,
  dispatch,
  setRefreshData,
}) => {
  const notyf = useContext(NotyfContext);
  const selectedLandlord = useSelector((state) => state.landlordswitcher);
  const { userAccessToken } = useAuth();
  const bearerToken = `Bearer ${userAccessToken}`;
  const [excellid, setExcellId] = useState();
  const [numUsersIsLoading, setNumUsersIsLoading] = useState(true);
  const [numAvailable, setNumAvailable] = useState(0);
  const [numInUse, setNumInUse] = useState(0);
  const [validated, setValidated] = useState(false);

  const [responseErrors, setResponseErrors] = useState(null);

  const rateLimitingAxios = createRateLimitedAxios();

  const [users, setUsers] = useState([]);
  const [invalidUsers, setInvalidUsers] = useState([]);
  const [progress, setProgress] = useState(0);

  const resetFormFields = () => {
    setValidated(false);
    setUsers([]);
    setProgress(0);
    setResponseErrors(null);
  };

  const handleOnRowsChange = (validRows, invalidRows) => {
    setUsers(validRows);
    setInvalidUsers(invalidRows);
  };

  // Handle when users is changed, such as on a row change
  useEffect(() => {
    if (
      invalidUsers?.length > 0 ||
      users?.length === 0 ||
      users?.length > numAvailable
    ) {
      setValidated(false);
    } else {
      setValidated(true);
    }
  }, [users, invalidUsers, numAvailable]);

  const handleCreateCredentialSubmit = async (event) => {
    event.preventDefault();

    let isErrorState = false;

    let receivedErrors = [];
    setProgress((progress) => 0);
    for (const user of users) {
      const createCredentialBody = {
        username: user.email,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        excellid: excellid,
        userlevel: user.userlevel,
        building_operator_id: selectedLandlord.landlordid,
      };

      try {
        const response = await rateLimitingAxios({
          method: "POST",
          url: apiConfig.dot11ApiUrl + "users/",
          data: createCredentialBody,
          headers: { Authorization: bearerToken },
        });
        if (response.data.success !== 1) {
          isErrorState = true;
          receivedErrors.push({
            username: user.email,
            error: response?.data?.error || "An unexpected error occurred",
          });
        }
      } catch (error) {
        isErrorState = true;
        receivedErrors.push({
          username: user.email,
          error: error?.response?.data?.error || "An error occurred.",
        });
      }
      setProgress((progress) => progress + 1);
    }
    if (isErrorState) {
      notyf.open({
        type: "warning",
        message: `An error occurred. ${receivedErrors.length}/${users.length} users failed to submit.`,
        duration: 3000,
        ripple: true,
        dismissible: true,
        position: { x: "right", y: "top" },
      });
      setResponseErrors(receivedErrors);
      console.error({
        message: "Error submitting multiple users",
        errors: receivedErrors,
      });
    } else {
      notyf.open({
        type: "success",
        message: `Successfully created ${users.length} users`,
        duration: 3000,
        ripple: true,
        dismissible: true,
        position: { x: "right", y: "top" },
      });
      toggle("addMultipleNewCredential");
      resetFormFields();
      setTimeout(() => dispatch(setRefreshData()), 2000);
    }
  };
  return (
    <Modal
      show={openModals["addMultipleNewCredential"]}
      onHide={() => {
        toggle("addMultipleNewCredential");
        resetFormFields();
      }}
      centered
    >
      <Form onSubmit={handleCreateCredentialSubmit}>
        <Modal.Header closeButton>
          <b>Admin Function:</b>&nbsp; Add Multiple New 802.1X Credentials
        </Modal.Header>
        <Modal.Body className="m-3">
          <div className="text-left my-0">
            <Form.Group className="mb-3">
              <h3>Select an account to wi-fi users for:</h3>
              <Form.Label>Company Name</Form.Label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={customerOptions}
                onChange={(e) => {
                  setExcellId(e.value);
                  resetFormFields();
                }}
                value={
                  excellid
                    ? customerOptions.find(
                        (option) => option.value === excellid
                      )
                    : null
                }
              />
            </Form.Group>
            {excellid ? (
              <NumUsersDisplay
                excellid={excellid}
                isLoading={numUsersIsLoading}
                setIsLoading={setNumUsersIsLoading}
                numAvailable={numAvailable}
                setNumAvailable={setNumAvailable}
                numInUse={numInUse}
                setNumInUse={setNumInUse}
              />
            ) : null}
            {!numUsersIsLoading && numAvailable > 0 && (
              <>
                <hr />
                <MultipleUserCsvInput
                  onRowsChange={handleOnRowsChange}
                  maxUsers={numAvailable}
                />
              </>
            )}
            {!numUsersIsLoading && numAvailable <= 0 && (
              <Alert className="my-3 p-2">
                This account does not have any available wi-fi users. &nbsp;
                <a
                  href="#"
                  onClick={() => {
                    toggle("addMultipleNewCredential");
                    toggle("wifiPurchase");
                  }}
                >
                  Request more.
                </a>
              </Alert>
            )}

            {progress !== 0 && progress !== users.length && (
              <Alert className="my-3 p-2" variant="info">
                Please wait... Uploading users {progress}/{users.length}
              </Alert>
            )}

            {responseErrors && (
              <Accordion className="my-3 p-2 text-lg">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Response Errors</Accordion.Header>
                  <Accordion.Body>
                    <code>
                      <pre>{JSON.stringify(responseErrors, null, 2)}</pre>
                    </code>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => toggle("addMultipleNewCredential")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant={!validated ? "secondary" : "info"}
            disabled={!validated}
            onClick={() => {
              // toggle("addMultipleNewCredential");
              setTimeout(() => dispatch(setRefreshData()), 2000);
            }}
          >
            Create Credentials
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddMultipleNew8021XCredentialModal;
